import React, { Suspense, lazy, useEffect } from "react";
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom"; // Changed from BrowserRouter
import Sidebar from "./components/Sidebar";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./index.css";
import NotFound from "./pages/NotFound";
import { SocketProvider } from "./context/SocketContext";
import { hasPermission } from "./utils/permissions";

// Lazy-loaded pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Bookings = lazy(() => import("./pages/Bookings"));
const Services = lazy(() => import("./pages/Services"));
const AdminManagement = lazy(() => import("./pages/AdminManagement"));
const HomepageEditor = lazy(() => import("./pages/HomepageEditor"));
const Customers = lazy(() => import("./pages/Customers"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Login = lazy(() => import("./pages/Login"));
const SessionCalendar = lazy(() => import("./pages/SessionCalendar"));
const StudioAssistant = lazy(() => import("./pages/StudioAssistant"));
const PrivacyTermsEditor = lazy(() => import("./pages/PrivacyTermsEditor"));

function Loader({ fullscreen = false }) {
  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#eef3f2]/80 backdrop-blur-sm">
        <div className="w-full max-w-xl rounded-2xl bg-white/90 p-6 shadow-[0_24px_56px_-24px_rgba(6,78,59,0.25)]">
          <div className="flex items-center gap-3">
            <div className="skeleton h-10 w-10 rounded-xl" />
            <div className="flex flex-col gap-2">
              <div className="skeleton h-4 w-40 rounded" />
              <div className="skeleton h-3 w-24 rounded" />
            </div>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="skeleton h-20 rounded-xl" />
            ))}
          </div>
          <div className="mt-3 skeleton h-40 w-full rounded-xl" />
        </div>
      </div>
    );
  }
  return (
    <div className="py-10">
      <div className="mx-auto grid max-w-3xl grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="skeleton h-24 rounded-2xl" />
        ))}
      </div>
      <div className="mx-auto mt-4 max-w-3xl skeleton h-48 rounded-2xl" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PermissionRoute({ permission, children }) {
  const { user } = useAuth();
  return hasPermission(user, permission) ? children : <Navigate to="/" replace />;
}

function ProtectedApp() {
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let section = "Dashboard";
    if (path.startsWith("/sessions")) section = "Sessions";
    else if (path.startsWith("/bookings")) section = "Bookings";
    else if (path.startsWith("/services")) section = "Services";
    else if (path.startsWith("/admin")) section = "Admin";
    else if (path.startsWith("/homepage-editor")) section = "Homepage Editor";
    else if (path.startsWith("/customers")) section = "Customers";
    else if (path.startsWith("/portfolio")) section = "Portfolio";
    else if (path.startsWith("/studio-assistant")) section = "Studio Assistant";
    else if (path.startsWith("/privacy-terms")) section = "Privacy & Terms";
    else if (path === "/") section = "Dashboard";
    else section = "Studio Kosmos Admin";

    document.title = `${section} | Studio Kosmos Admin`;
  }, [location.pathname]);

  if (loading) return <Loader fullscreen />;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <main className="ml-[280px] flex-1 overflow-y-auto overflow-x-hidden bg-[#eef3f2]">
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/sessions" element={<PermissionRoute permission="sessions"><SessionCalendar /></PermissionRoute>} />
            <Route path="/bookings" element={<PermissionRoute permission="bookings"><Bookings /></PermissionRoute>} />
            <Route path="/services" element={<PermissionRoute permission="services"><Services /></PermissionRoute>} />
            <Route path="/admin" element={<AdminManagement />} />
            <Route path="/homepage-editor" element={<PermissionRoute permission="homepage"><HomepageEditor /></PermissionRoute>} />
            <Route path="/customers" element={<PermissionRoute permission="users"><Customers /></PermissionRoute>} />
            <Route path="/portfolio" element={<PermissionRoute permission="portfolio"><Portfolio /></PermissionRoute>} />
            <Route path="/studio-assistant" element={<PermissionRoute permission="studio-assistant"><StudioAssistant /></PermissionRoute>} />
            <Route path="/privacy-terms" element={<PermissionRoute permission="privacy-terms"><PrivacyTermsEditor /></PermissionRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Loader fullscreen />;
  return user ? (
    <Navigate to="/" replace />
  ) : (
    <Suspense fallback={<Loader fullscreen />}>
      <Login />
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router> {/* HashRouter instead of BrowserRouter */}
        <ScrollToTop />
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route
            path="*"
            element={
              <SocketProvider>
                <ProtectedApp />
              </SocketProvider>
            }
          />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          limit={3}
        />
      </Router>
    </AuthProvider>
  );
}

export default App;